import React from 'react';
import { Company } from '../types/Company';
import { useCompany } from '../contexts/CompanyContext';
import { useToast } from '../../shared/contexts/ToastContext';

interface CompanyDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: Company | null;
}

const CompanyDetailModal: React.FC<CompanyDetailModalProps> = ({ isOpen, onClose, company }) => {
  const { downloadGlossaryFile /* NORMALIZE FIELD: , downloadNormalizeFile */ } = useCompany();
  const { success, error } = useToast();

  const handleDownloadGlossary = async () => {
    if (!company) return;
    try {
      await downloadGlossaryFile(company);
      success('Dosya İndirildi', 'Glossary dosyası başarıyla indirildi.');
    } catch (err) {
      error('İndirme Hatası', 'Glossary dosyası indirilirken hata oluştu.');
    }
  };

  /* NORMALIZE FIELD - Uncomment to enable
  const handleDownloadNormalize = async () => {
    if (!company) return;
    try {
      await downloadNormalizeFile(company);
      success('Dosya İndirildi', 'Normalize dosyası başarıyla indirildi.');
    } catch (err) {
      error('İndirme Hatası', 'Normalize dosyası indirilirken hata oluştu.');
    }
  };
  */

  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-2-5h6m-6 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0h6m-6 0a2 2 0 002 2h2a2 2 0 002-2"/>
              </svg>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {company.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Şirket Detayları
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
              Temel Bilgiler
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Şirket Adı
                </label>
                <p className="text-gray-900 dark:text-white font-medium">
                  {company.name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Oluşturulma Tarihi
                </label>
                <p className="text-gray-900 dark:text-white">
                  {new Date(company.createdAt).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Son Güncellenme
                </label>
                <p className="text-gray-900 dark:text-white">
                  {new Date(company.updatedAt).toLocaleDateString('tr-TR', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Şirket ID
                </label>
                <p className="text-gray-900 dark:text-white font-mono text-sm">
                  {company.id}
                </p>
              </div>
            </div>
            
            {/* Created By Information */}
            {(company.created_by_username || (company.createdBy && company.createdBy.name)) && (
              <div className="mt-4 p-4 bg-blue-50/50 dark:bg-blue-900/20 rounded-lg border border-blue-200/50 dark:border-blue-800/50">
                <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-2 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  Oluşturan Kişi
                </label>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
                      {(company.created_by_username || company.createdBy?.name || 'U').charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-blue-900 dark:text-blue-100 font-medium">
                      {company.created_by_username || company.createdBy?.name || 'Bilinmeyen'}
                    </p>
                    {company.createdBy?.email && (
                      <p className="text-blue-600 dark:text-blue-400 text-sm">
                        {company.createdBy.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {company.description && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Açıklama
                </label>
                <p className="text-gray-900 dark:text-white">
                  {company.description}
                </p>
              </div>
            )}
          </div>

          {/* Files Information */}
          <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Yüklü Dosyalar
            </h3>
            
            {/* NORMALIZE FIELD - Change to: grid-cols-1 md:grid-cols-2 */}
            <div className="grid grid-cols-1 gap-6">
              {/* Glossary File */}
              <div className="bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 border border-green-200 dark:border-green-800/30 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white flex items-center text-lg">
                    <svg className="w-5 h-5 mr-3 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    Glossary Dosyası
                  </h4>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 border border-green-300 dark:border-green-700">
                    <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                    Hazır
                  </span>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dosya Adı</p>
                    <p className="text-sm font-mono text-gray-900 dark:text-white bg-white/60 dark:bg-gray-800/60 px-3 py-2 rounded-lg border">
                      {company.glossaryFileName || `${company.name}_glossary.xlsx`}
                    </p>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
                    Excel formatında glossary dosyası
                  </p>
                  <button
                    onClick={handleDownloadGlossary}
                    className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 active:bg-green-800 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    Dosyayı İndir
                  </button>
                </div>
              </div>

              {/* NORMALIZE FIELD - Uncomment to enable
              <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10 border border-blue-200 dark:border-blue-800/30 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white flex items-center text-lg">
                    <svg className="w-5 h-5 mr-3 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    Normalize Dosyası
                  </h4>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300 border border-blue-300 dark:border-blue-700">
                    <svg className="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </svg>
                    Hazır
                  </span>
                </div>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dosya Adı</p>
                    <p className="text-sm font-mono text-gray-900 dark:text-white bg-white/60 dark:bg-gray-800/60 px-3 py-2 rounded-lg border">
                      {company.normalizeFileName || `${company.name}_normalize.xlsx`}
                    </p>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-4">
                    Excel formatında normalize dosyası
                  </p>
                  <button
                    onClick={handleDownloadNormalize}
                    className="w-full inline-flex items-center justify-center px-4 py-3 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                    Dosyayı İndir
                  </button>
                </div>
              </div>
              */}
            </div>
          </div>

          {/* Statistics (placeholder for future features) */}
          <div className="bg-gray-50/50 dark:bg-gray-900/50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
              İstatistikler
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#02c2ff]">
                  {/* NORMALIZE FIELD - Update condition to include normalize: 
                  {((company.glossaryFileName || company.glossary_file) && (company.normalizeFileName || company.normalize_file)) ? '2' : 
                   (company.glossaryFileName || company.glossary_file || company.normalizeFileName || company.normalize_file) ? '1' : '0'}
                  */}
                  {(company.glossaryFileName || company.glossary_file) ? '1' : '0'}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Yüklü Dosya</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-500">0</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Çeviri Projesi</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-500">0</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Tamamlanan</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-500">
                  {Math.floor((new Date().getTime() - new Date(company.createdAt).getTime()) / (1000 * 60 * 60 * 24))}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">Gün Önce</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-[#02c2ff] hover:bg-[#0199cc] text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all duration-200"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetailModal;
